import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle } from 'lucide-react';
import RyanHeader from '../components/RyanHeader';
import RyanPwaInstallGuide from '../components/RyanPwaInstallGuide';
import './RyanInvitePage.css';

type PageState = 'loading' | 'invalid' | 'used' | 'expired' | 'ready' | 'success';

interface InviteInfo {
  email: string;
  product: { title: string; periodo: string | null; priceCents: number };
}

const RyanInvitePage: React.FC = () => {
  const { token } = useParams<{ token?: string }>();
  const navigate = useNavigate();

  const [state, setState]       = useState<PageState>('loading');
  const [info, setInfo]         = useState<InviteInfo | null>(null);
  const [invalidReason, setInvalidReason] = useState('');

  const [username, setUsername]           = useState('');
  const [password, setPassword]           = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError]                 = useState('');
  const [submitting, setSubmitting]       = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); setInvalidReason('Link inválido.'); return; }

    fetch(`/api/ryan/convite/${token}`)
      .then(r => r.json())
      .then(d => {
        if (!d.valid) {
          const reasons: Record<string, string> = {
            not_found: 'Este link de convite não existe.',
            used: 'Este convite já foi utilizado.',
            expired: 'Este convite expirou.',
          };
          setInvalidReason(reasons[d.reason] || 'Convite inválido.');
          setState(d.reason === 'used' ? 'used' : d.reason === 'expired' ? 'expired' : 'invalid');
          return;
        }
        setInfo({ email: d.email, product: d.product });
        setState('ready');
      })
      .catch(() => { setState('invalid'); setInvalidReason('Erro de conexão.'); });
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('A senha deve ter no mínimo 6 caracteres.'); return; }
    if (password !== confirmPassword) { setError('As senhas não coincidem.'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`/api/ryan/convite/${token}/aceitar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Erro ao criar conta.'); return; }
      setState('success');
    } catch {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ryan-invite-root">
      <RyanHeader showLogout={false} />

      <div className="ryan-invite-body">
        {state === 'loading' && (
          <div className="ryan-invite-spinner-wrap">
            <div className="ryan-invite-spinner" />
            <span className="ryan-invite-spinner-text">Verificando convite…</span>
          </div>
        )}

        {(state === 'invalid' || state === 'used' || state === 'expired') && (
          <div className="ryan-invite-card">
            <div className="ryan-invite-icon">
              <AlertCircle size={40} color="#dc2626" />
            </div>
            <h2 className="ryan-invite-title">Convite inválido</h2>
            <p className="ryan-invite-desc">{invalidReason}</p>
            <button className="ryan-invite-btn" onClick={() => navigate('/ryan/login')}>
              Ir para login
            </button>
          </div>
        )}

        {state === 'ready' && info && (
          <div className="ryan-invite-card">
            <h2 className="ryan-invite-title">Criar sua conta</h2>
            <p className="ryan-invite-desc">
              Você foi convidado para acessar <strong>{info.product.title}</strong>.<br />
              Conta vinculada ao email <strong>{info.email}</strong>.
            </p>

            <form className="ryan-invite-form" onSubmit={handleSubmit}>
              {error && <div className="ryan-invite-error">{error}</div>}

              <div className="ryan-invite-field">
                <label className="ryan-invite-label">Nome de usuário</label>
                <input
                  className="ryan-invite-input"
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="seunome"
                  required
                  autoFocus
                />
              </div>

              <div className="ryan-invite-field">
                <label className="ryan-invite-label">Senha</label>
                <input
                  className="ryan-invite-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                />
              </div>

              <div className="ryan-invite-field">
                <label className="ryan-invite-label">Confirmar senha</label>
                <input
                  className="ryan-invite-input"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repita a senha"
                  required
                />
              </div>

              <button type="submit" className="ryan-invite-btn" disabled={submitting}>
                {submitting ? 'Criando conta…' : 'Criar conta e acessar'}
              </button>
            </form>
          </div>
        )}

        {state === 'success' && (
          <div className="ryan-invite-card">
            <div className="ryan-invite-icon">
              <CheckCircle size={40} color="#0D3B0E" />
            </div>
            <h2 className="ryan-invite-title ryan-invite-success-text">Sua conta tá pronta! 🎉</h2>
            <p className="ryan-invite-desc">
              Uma dica antes de começar: dá pra ter o GRAIA Medicina como um app de verdade no seu celular, é rapidinho:
            </p>

            <div className="ryan-invite-pwa-guide">
              <RyanPwaInstallGuide />
            </div>

            <button className="ryan-invite-btn" onClick={() => navigate('/ryan/login')}>
              Ir para login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RyanInvitePage;
